import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Lock, ShieldAlert, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  buildContentTrackingMetadata,
  getApiBaseUrl,
  storeContentSession,
} from "../lib/gatedContentTracking";

const EmailGate = ({
  onAccessGranted,
  title = "Please enter your email to view the slideshow",
  description = null,
  submitLabel = "View slideshow",
  agreementNoun = "the slideshow",
  confidentialityText = "The information in this deck is strictly confidential. Sharing any information without prior written consent from Asha Health, Inc. would be a violation of the NDA you accepted in the previous step.",
  professionalConductText = "Please be professional. Do not reach out to any offices referenced in this deck — including practices featured in testimonials, case studies, or customer logos — without prior written permission from our team. Unauthorized outreach has led to complaints from our customers in the past and may result in legal action.",
  sessionStorageKey = "asha_slideshow_session_id",
  contentId,
  contentType,
  contentTitle,
  contentRoute,
}) => {
  const [email, setEmail] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showConfidentialityWarning, setShowConfidentialityWarning] =
    useState(false);
  const [showProfessionalConductWarning, setShowProfessionalConductWarning] =
    useState(false);
  const renderedDescription = description ?? (
    <>
      Access to the{" "}
      <span className="text-blue-400 font-semibold">Asha Health</span>{" "}
      interactive tour is restricted.
    </>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isAgreed) {
      setError("Please agree to the Privacy Policy and NDA.");
      return;
    }

    setIsLoading(true);

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }

    try {
      const apiUrl = getApiBaseUrl();
      const contentMetadata = buildContentTrackingMetadata({
        contentId,
        contentType,
        contentTitle,
        contentRoute,
      });
      const response = await fetch(`${apiUrl}/api/slideshow/access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          ndaSigned: true, // Assuming checkbox covers both
          privacyPolicySigned: true,
          ...contentMetadata,
        }),
      });

      // Handle non-JSON responses
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(
          "Server returned an invalid response. Please try again."
        );
      }

      if (!response.ok) {
        throw new Error(
          data.error ||
            `Server error: ${response.status} ${response.statusText}`
        );
      }

      setIsSuccess(true);

      // Store session ID for analytics
      if (data.sessionId) {
        storeContentSession(data.sessionId, sessionStorageKey, contentMetadata);
      }

      // Show confidentiality warning after brief success state
      setTimeout(() => {
        setShowConfidentialityWarning(true);
      }, 1000);
    } catch (err) {
      console.error("EmailGate error:", err);
      // Provide more specific error messages
      if (err instanceof TypeError && err.message.includes("fetch")) {
        setError(
          "Unable to connect to server. Please check your connection and try again."
        );
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-950/90 backdrop-blur-md p-4"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[120px]" />
      </div>

      <AnimatePresence mode="wait">
        {showProfessionalConductWarning ? (
          <motion.div
            key="professional-conduct"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: -20, opacity: 0 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="relative w-full max-w-md bg-neutral-900/50 border border-white/10 rounded-3xl p-8 shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="mb-6 p-4 rounded-full bg-rose-500/10 border border-rose-500/20 shadow-[0_0_20px_rgba(244,63,94,0.15)]">
                <Users className="w-8 h-8 text-rose-400" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-6">
                Important Notice
              </h2>

              <p className="text-white font-bold text-base leading-relaxed mb-8">
                {professionalConductText}
              </p>

              <Button
                onClick={() => onAccessGranted(email)}
                className="w-full h-12 text-lg font-semibold rounded-xl bg-blue-600 hover:bg-blue-500 text-white hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all duration-300"
              >
                I Understand
              </Button>
            </div>
          </motion.div>
        ) : showConfidentialityWarning ? (
          <motion.div
            key="confidentiality"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: -20, opacity: 0 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="relative w-full max-w-md bg-neutral-900/50 border border-white/10 rounded-3xl p-8 shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="mb-6 p-4 rounded-full bg-amber-500/10 border border-amber-500/20 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
                <ShieldAlert className="w-8 h-8 text-amber-400" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-6">
                Confidentiality Notice
              </h2>

              <p className="text-white font-bold text-base leading-relaxed mb-8">
                {confidentialityText}
              </p>

              <Button
                onClick={() => setShowProfessionalConductWarning(true)}
                className="w-full h-12 text-lg font-semibold rounded-xl bg-blue-600 hover:bg-blue-500 text-white hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all duration-300"
              >
                I Understand
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="email-form"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: -20, opacity: 0 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="relative w-full max-w-md bg-neutral-900/50 border border-white/10 rounded-3xl p-8 shadow-2xl overflow-hidden"
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="mb-6 p-4 rounded-full bg-blue-500/10 border border-blue-500/20 shadow-[0_0_20px_rgba(59,130,246,0.15)]">
                <Lock className="w-8 h-8 text-blue-400" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-2">
                {title}
              </h2>
              <p className="text-neutral-400 mb-8 leading-relaxed text-sm">
                {renderedDescription}
              </p>

              <form onSubmit={handleSubmit} className="w-full space-y-6">
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-neutral-800/50 border-white/10 text-white placeholder:text-neutral-500 focus:border-blue-500/50 focus:ring-blue-500/20 h-12 rounded-xl transition-all"
                      disabled={isLoading || isSuccess}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 text-left">
                  <Checkbox
                    id="terms"
                    checked={isAgreed}
                    onCheckedChange={setIsAgreed}
                    className="mt-1 border-white/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm text-neutral-400 leading-snug cursor-pointer select-none"
                  >
                    By viewing {agreementNoun} you agree to our{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                    >
                      Privacy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/nda"
                      target="_blank"
                      className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                    >
                      NDA
                    </a>
                    .
                  </label>
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded-lg"
                  >
                    {error}
                  </motion.p>
                )}

                <Button
                  type="submit"
                  className={`w-full h-12 text-lg font-semibold rounded-xl transition-all duration-300 ${
                    isSuccess
                      ? "bg-green-500 hover:bg-green-600 text-white"
                      : "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-[0_0_20px_rgba(37,99,235,0.3)]"
                  }`}
                  disabled={isLoading || isSuccess || !isAgreed}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </div>
                  ) : isSuccess ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Access Granted</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{submitLabel}</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EmailGate;
